import React, { useEffect, useMemo, useState } from 'react';
import { OfficeEntity } from '@features/desks/services/AdvanceHotDeskingService';
import { OfficeCapacityLabel } from '@shared/ui/molecules/OfficeCapacityLabel';
import { CapacityResponse } from "@features/desks/services/OfficeCapacityService";
import { BodyLarge } from '@shared/ui/atoms/fonts/Body';
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { selectActiveDay, selectActivePeriod } from "../../../../store/editMovements.duck";
import { Column, Row, SpaceBetweenRow } from '@shared/ui/atoms/StructuralLayout';
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import { Period, SimpleUserWithLocation } from '@features/whereabouts/models/movements.models';
import { bookSingleDayMovement } from "../../../../store/officeUsage.duck";
import { calculateAmSpacesAvailable, calculatePmSpacesAvailable } from "@shared/utils/OfficeHelper";
import { selectCurrentUser, selectIsInTeams } from "@shared/store/auth.duck";
import { getUsersInOfficeOn } from '@features/whereabouts/services/Movements';
import Colours from '@shared/ui/atoms/Colours';
import { ExpandableUserList } from "./ExpandableUserList";
import Icon, { IconTypes } from '@shared/ui/atoms/icon/Icon';
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';

export const AUTO_EXPAND_LIMIT = 8;

export function DailyOfficeArea(props: Props) {
  const {entity, capacity, initialOpenState} = props;
  const dispatch = useDispatch();
  const [usersInArea, setUsersInArea] = useState<SimpleUserWithLocation[]>([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isVisible, setIsVisible] = useState(initialOpenState);
  const currentUser = useSelector(selectCurrentUser);
  const selectedDate = useSelector(selectActiveDay);
  const activePeriod = useSelector(selectActivePeriod);
  const amSpacesAvailable = useMemo(() => calculateAmSpacesAvailable(capacity), [capacity]);
  const pmSpacesAvailable = useMemo(() => calculatePmSpacesAvailable(capacity), [capacity]);
  const allDaySpacesAvailable = useMemo(() => amSpacesAvailable > 0 && pmSpacesAvailable > 0, [amSpacesAvailable, pmSpacesAvailable])
  const isInTeams = useSelector(selectIsInTeams);

  const bookSingleDay = (period: Period) => {
    if (selectedDate && amSpacesAvailable > 0 && pmSpacesAvailable > 0) {
      dispatch(bookSingleDayMovement({
        date: selectedDate,
        office: entity,
        userId: currentUser?.id,
        period: period
      }));
    }
  }

  const amUsed = capacity?.amUsed;
  const pmUsed = capacity?.pmUsed;

  // The list loads whenever the area is open, whatever the count says, and again when the date or
  // the count changes. It's cleared first so another date's list never shows under this count, and a
  // response that arrives after a newer request is ignored.
  useEffect(() => {
    setUsersInArea([]);
    setHasLoaded(false);
    if (!isVisible || !selectedDate) return;

    let isCurrent = true;
    getUsersInOfficeOn(selectedDate, entity.id)
      .then((resp: SimpleUserWithLocation[]) => {
        if (isCurrent) setUsersInArea(resp);
      })
      .catch(() => {})
      .finally(() => {
        if (isCurrent) setHasLoaded(true);
      });
    return () => {
      isCurrent = false;
    };
  }, [amUsed, pmUsed, entity.id, selectedDate, isVisible]);

  const onRowClick = (event: Event) => {
    event.stopPropagation();
    setIsVisible(!isVisible);
  }

  return (
    <AreaContainer data-test={entity.label}>
      <ExpandableRow onClick={(event: Event) => onRowClick(event)}>
        <Column>
          <BodyLarge weight={600} style={{lineHeight: '26px'}}>{entity.label}</BodyLarge>
          <Row>
            <Icon icon={isVisible ? IconTypes.Down : IconTypes.Next} className={"expandableRow__icon"} color={"grey"} />
            <OfficeCapacityLabel capacity={capacity} />
          </Row>
        </Column>

        <div style={{alignItems: 'center', display: 'flex'}}>
          {entity.floor && !isInTeams && <OutlineButton text={'button.view'} size={ButtonSize.Small} style={{marginRight: 8}}
                                          click={() => window.location.href = `/whereabouts/company/all?office=${entity.id}&view=floor`} />}
          {!isInTeams && <OutlineButton size={ButtonSize.Small}
                                          disabled={!entity.active || !allDaySpacesAvailable}
                                          click={() => bookSingleDay(activePeriod)}
                                          text="button.book"
                                          dataTest="book-into-office-button" />}
        </div>
      </ExpandableRow>

      <ExpandableUserList office={entity} users={usersInArea} isVisible={isVisible} isLoading={!hasLoaded} />

    </AreaContainer>
  )
}

interface Props {
  entity: OfficeEntity;
  initialOpenState: boolean;
  capacity?: CapacityResponse;
}

const AreaContainer = styled.div`
  border-bottom: 1px solid ${Colours.mildGrey};
  &:last-child {
    border: none;
  }
`

const ExpandableRow = styled(SpaceBetweenRow)`
  transition: background-color .2s;
  &:hover {
    background-color: ${Colours.blue05};
  }
  .expandableRow__icon {
    margin-right: 8px;
  }
`
